import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Eye, EyeOff, LogIn, Building2 } from 'lucide-react';
import { useAuthStore } from './authStore';
import { extractTenantSubdomain } from '@/utils/tenantHostname';

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
  tenant: z.string().min(1, 'Tenant is required'),
  rememberMe: z.boolean().optional(),
});

type LoginFormData = z.infer<typeof loginSchema>;

export const LoginPage: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [tenantName, setTenantName] = useState<string>('');
  const [tenantLoading, setTenantLoading] = useState(false);
  const [tenantError, setTenantError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { login } = useAuthStore();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  // Watch the tenant field value
  const tenantValue = watch('tenant');

  // Fetch tenant name by subdomain
  const fetchTenantName = async (subdomain: string) => {
    if (!subdomain || subdomain.trim() === '') {
      setTenantName('');
      setTenantError(null);
      return;
    }

    // Special handling for 'platform' subdomain - Super Admin access
    if (subdomain.toLowerCase() === 'platform') {
      setTenantName('Platform Admin');
      setTenantError(null);
      console.log('🏢 Platform admin access');
      return;
    }

    setTenantLoading(true);
    setTenantError(null);
    try {
      const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || 'http://localhost:4000/api/v1';
      const response = await fetch(`${API_BASE_URL}/tenants/public/subdomain/${subdomain}`);
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data?.name) {
          setTenantName(data.data.name);
          setTenantError(null);
          console.log('🏢 Fetched tenant name:', data.data.name);
        } else {
          setTenantName('');
          setTenantError('Invalid tenant');
          console.log('🏢 Invalid tenant response:', data);
        }
      } else {
        setTenantName('');
        setTenantError('Invalid tenant');
        console.log('🏢 Failed to fetch tenant, status:', response.status);
      }
    } catch (error) {
      console.error('Error fetching tenant name:', error);
      setTenantName('');
      setTenantError('Invalid tenant');
    } finally {
      setTenantLoading(false);
    }
  };

  // Auto-detect tenant from subdomain
  useEffect(() => {
    const detectTenantFromSubdomain = async () => {
      const subdomain = extractTenantSubdomain(window.location.hostname, {
        allowPlatform: true,
      });

      if (subdomain) {
        setValue('tenant', subdomain);
        console.log('🏢 Auto-detected tenant from subdomain:', subdomain);
        // Fetch tenant name (platform subdomain is handled specially in fetchTenantName)
        await fetchTenantName(subdomain);
      } else {
        // No subdomain detected, leave tenant field blank
        setValue('tenant', '');
        setTenantName('');
        setTenantError(null);
        console.log('🏢 No subdomain detected - tenant field left blank');
      }
    };

    detectTenantFromSubdomain();
  }, [setValue]);

  // State to track if tenant was auto-detected
  const [isTenantAutoDetected, setIsTenantAutoDetected] = useState(false);
  // State to track if subdomain is present in URL
  const [hasSubdomain, setHasSubdomain] = useState(false);

  useEffect(() => {
    const subdomain = extractTenantSubdomain(window.location.hostname, {
      allowPlatform: true,
    });

    if (subdomain) {
      setIsTenantAutoDetected(true);
      setHasSubdomain(true);
      return;
    }

    setIsTenantAutoDetected(false);
    setHasSubdomain(false);
  }, []);

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setLoginError(null); // Clear previous errors

    try {
      console.log('Attempting login with:', { email: data.email, tenant: data.tenant, rememberMe: data.rememberMe });
      await login(data.email, data.password, data.tenant, data.rememberMe);

      // Get user info from auth store to determine navigation
      const { user } = useAuthStore.getState();

      toast.success('Login successful! Welcome to ERPCA.');

      // Navigate based on user type and role
      if (user?.isPlatformUser) {
        // Platform users go to platform admin
        navigate('/platform');
      } else if (user?.roles?.includes('FIRM_ADMIN') || user?.roles?.includes('FirmAdmin')) {
        // Tenant admins go to firm admin dashboard
        navigate('/firm-admin');
      } else if (user?.roles?.includes('Manager') || user?.roles?.includes('Staff')) {
        // Staff and Manager users go to user dashboard
        navigate('/user-dashboard');
      } else {
        // Other users go to main dashboard
        navigate('/dashboard');
      }
    } catch (error: any) {
      console.error('Login error:', error);

      // Show detailed error message
      let errorMessage = 'Login failed. Please check your credentials.';

      if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      }

      console.log('Showing error message:', errorMessage);
      setLoginError(errorMessage);

      toast.error(errorMessage, {
        duration: 5000, // Show for 5 seconds
        position: 'top-center',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pastel-blue via-white to-pastel-green flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-soft-lg p-8 border border-white/20">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Building2 className="w-8 h-8 text-primary-500" />
            </div>
            <h1 className="text-3xl font-display font-bold text-primary-500 mb-2">
              Welcome to ERPCA
            </h1>
            <p className="text-gray-600 font-body">
              Sign in to your accounting practice
            </p>
          </div>



          {/* Login Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Tenant Field - Only show when subdomain is present */}
            {hasSubdomain && (
              <div className="form-group">
                <label className="form-label">
                  Tenant / Firm
                </label>
                <input
                  {...register('tenant')}
                  type="hidden"
                  id="tenant"
                />
                <div className={`px-4 py-3 bg-gray-50 border rounded-lg ${tenantError ? 'border-red-300' : 'border-gray-300'}`}>
                  {tenantValue ? (
                    <div className="space-y-1">
                      <div className="flex items-center justify-between">
                        {tenantLoading ? (
                          <span className="text-gray-500">Loading...</span>
                        ) : (
                          <>
                            <span className={`font-medium ${tenantError ? 'text-red-600' : 'text-gray-900'}`}>
                              {tenantName || tenantValue}
                            </span>
                            {isTenantAutoDetected && !tenantError && tenantName && (
                              <span className="text-xs text-green-600 font-medium">✓ Auto-detected</span>
                            )}
                          </>
                        )}
                      </div>
                      {tenantError && (
                        <p className="text-xs text-red-600">{tenantError}</p>
                      )}
                    </div>
                  ) : (
                    <span className="text-gray-400 italic">No subdomain detected</span>
                  )}
                </div>
                {errors.tenant && (
                  <p className="form-error">{errors.tenant.message}</p>
                )}
              </div>
            )}

            {!hasSubdomain && (
              <input
                {...register('tenant')}
                type="hidden"
                value=""
              />
            )}

            {/* Show login form only if tenant is valid or no subdomain */}
            {!tenantError && (
              <>
                {/* Email Field */}
                <div className="form-group">
                  <label htmlFor="email" className="form-label">
                    Email Address
                  </label>
                  <input
                    {...register('email')}
                    type="email"
                    id="email"
                    className="input"
                    placeholder="Enter your email"
                  />
                  {errors.email && (
                    <p className="form-error">{errors.email.message}</p>
                  )}
                </div>

                {/* Password Field */}
                <div className="form-group">
                  <label htmlFor="password" className="form-label">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      {...register('password')}
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      className="input pr-12"
                      placeholder="Enter your password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {showPassword ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="form-error">{errors.password.message}</p>
                  )}
                </div>

                {/* Remember Me Checkbox */}
                <div className="flex items-center">
                  <input
                    id="rememberMe"
                    type="checkbox"
                    {...register('rememberMe')}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="rememberMe" className="ml-2 block text-sm text-gray-700">
                    Remember me for 30 days
                  </label>
                </div>

                {/* Login Error Display */}
                {loginError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-red-800">
                          {loginError}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full btn-primary py-3 text-base font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Signing in...
                    </div>
                  ) : (
                    <div className="flex items-center justify-center">
                      <LogIn className="w-5 h-5 mr-2" />
                      Sign In
                    </div>
                  )}
                </button>
              </>
            )}

            {/* Show registration link when tenant is invalid */}
            {tenantError && (
              <div className="text-center py-6">
                <p className="text-gray-700 mb-4">
                  The tenant subdomain you entered is not recognized.
                </p>
                <p className="text-sm text-gray-600">
                  Don't have an account?{' '}
                  <a href="/register" className="text-primary-500 hover:text-primary-600 font-medium">
                    Start your free trial
                  </a>
                </p>
              </div>
            )}
          </form>

          {/* Footer */}
          <div className="mt-8 text-center space-y-4">
            <p className="text-sm text-gray-600">
              <Link to="/forgot-password" className="text-primary-500 hover:text-primary-600 font-medium">
                Forgot your password?
              </Link>
            </p>
            {/* Show registration link only when NO subdomain is present */}
            {!hasSubdomain && (
              <p className="text-sm text-gray-600">
                Don't have an account?{' '}
                <a href="/register" className="text-primary-500 hover:text-primary-600 font-medium">
                  Start your free trial
                </a>
              </p>
            )}
            <p className="text-sm text-gray-500">
              Need help? Contact your system administrator
            </p>
          </div>
        </div>

        {/* System Status */}
        <div className="mt-6 text-center">
          <div className="flex items-center justify-center space-x-4 text-sm text-gray-500">
            <span className="flex items-center">
              <div className="w-2 h-2 bg-secondary-500 rounded-full mr-2"></div>
              Backend API: Ready
            </span>
            <span className="flex items-center">
              <div className="w-2 h-2 bg-secondary-500 rounded-full mr-2"></div>
              Frontend: Ready
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
